// frontend/src/hooks/useSwipeRow.js
/**
 * useSwipeRow — horizontal swipe gesture on a row, via Pointer Events.
 *
 * @description
 *   The ONE swipe hook of the house (moved here from
 *   `notifications/useSwipeRow.js`, 2026-10-09, UI-Durchgang 2 Teil 5 / M2).
 *   Returns spreadable handlers for the row element plus the live `dx` the
 *   caller renders as a transform. The gesture is a SHORTCUT, never a path:
 *   every action it can fire must also be reachable by a single tap (WCAG
 *   2.5.1 — see the gesture contract G1–G15 in
 *   `docs/knowledge/design-language/pattern-library.md`).
 *
 *   🔴 THE DOCSTRING HERE USED TO BE WRONG, and it is the reason the coarse
 *   gate exists. It claimed: "Works for touch (mobile) AND trackpad/mouse
 *   (desktop two-finger horizontal swipe maps to pointermove deltas)." A
 *   two-finger trackpad swipe does NOT produce a `pointerdown` — Pointer
 *   Events require a pressed button, and `onPointerDown` reacts to exactly
 *   that. What actually fired on a desktop was a MOUSE DRAG with the button
 *   held, i.e. an accidental swipe while selecting text. G1 closes that:
 *   without `(pointer: coarse)` the hook is inert.
 *
 * @useWhen
 *   - A dense row should offer a reveal-and-commit swipe (mute, archive)
 *   - The same action is already reachable by tap, ⋯-menu or a sheet
 *
 * @dontUseFor
 *   - A gesture that fires a server action WITHOUT an undo → use reveal + tap
 *   - Desktop-only surfaces — the coarse gate makes the hook inert there
 *   - Horizontal paging/carousels (that is `SwipeableDrawer`/framer-motion)
 *
 * @related
 *   - `components/notifications/NotificationCenter.jsx` — the only consumer
 *   - `rma/RMACasesDashboardBody.jsx` — the RMA card; it uses framer-motion
 *     `drag` (coarse pointer only, G1), NOT this hook, but obeys the same
 *     G-rules
 *
 * @reuseHints
 *   - RMA inbox cards / PO inbox cards (UI-Durchgang 2 Teil 5, M3)
 *
 * @props
 *   onSwipeLeft/onSwipeRight — fired past the threshold within the time limit
 *   threshold          — px floor; the row-width fraction can only raise it
 *   thresholdFraction  — share of the row width (G5-B)
 *   maxDuration        — ms cutoff; a slow drag is not a swipe (G13)
 *   verticalCancel     — px of vertical travel that aborts the gesture (G4)
 *   enabled            — `true` (Vorgabe, = das bisherige Verhalten) | `false`:
 *     die Geste ist **pausiert** — kein Start, keine Verschiebung, keine
 *     Ausführung. 🔴 G7 braucht das: im Auswahlmodus (§11.6, G0.9) gehört die
 *     waagerechte Bewegung der Auswahl, nicht der Wischaktion; „pausiert" ist
 *     der Grund, warum der Auswahlmodus ein MODUS ist und keine zusätzliche
 *     Wischrichtung. Ohne die Prop ist der Baum Zeichen für Zeichen der alte:
 *     kein neuer Effekt, kein neuer Zuhörer, kein neues Attribut (G0.9) —
 *     **3 Hook-Verbraucher** (`NotificationCenter`, `POInbox`, `RMAInbox`),
 *     dazu **2 Konstanten-Importeure** (`wischRuecknahme.js` mit `SWIPE_-
 *     DIRECTION_LOCK_PX`; `useRowSelection.js` mit `readCoarsePointer`,
 *     `SWIPE_EDGE_GUARD_PX` und `SWIPE_DIRECTION_LOCK_PX`) — beide ohne Hook.
 *     🔴 Die Zahl war hier als „5 Verbraucher" notiert; die 5 kam aus dem
 *     Plan-Befehl, der auch die blosse **Nennung** in
 *     `RMACasesDashboardBody.jsx` mitzählte (R15 W3). Danach stand hier „3 + 1"
 *     — auch das war zu klein: `useRowSelection.js` fehlte, obwohl der nächste
 *     Absatz genau diesen Verbraucher nennt („der Auswahlmodus bindet seinen
 *     Langdruck an dieselbe Abfrage", R15 W-2). Kode-wahr sind 3 + 2.
 *
 * @returns { handlers, dx, swiping }
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/** G1 — die Geste ist nur auf einem groben Zeiger aktiv. */
const COARSE_POINTER_QUERY = '(pointer: coarse)'

/** G3 — Systemrand, den iOS/Android fuer „zurueck"/„vorwaerts" beanspruchen. */
export const SWIPE_EDGE_GUARD_PX = 24

/** G4 — ab hier ist die Richtung entschieden, **wenn die waagerechte Achse
 *  dominiert** (`|dx| >= |dy|`). Frueh, sonst fuehlt es traege — aber nicht
 *  allein: ein Daumen, der beim senkrechten Rollen seitlich driftet, darf die
 *  Zeile nicht kapern. */
export const SWIPE_DIRECTION_LOCK_PX = 10

/** G5-B — Mindestweg in px. 🔴 Hebt den alten Wert 64 an: zu leichtfertig fuer
 *  eine Server-Aktion, eine waagerechte Zuckung beim Rollen liegt in dieser
 *  Groessenordnung. */
export const SWIPE_MIN_THRESHOLD_PX = 96

/** G5-B — Anteil der Zeilenbreite; der groessere der beiden Werte gewinnt. */
export const SWIPE_THRESHOLD_FRACTION = 0.25

/** G12 — Dauer der Haptik beim AUSFUEHREN (nicht beim Aufdecken). */
export const SWIPE_HAPTIC_MS = 8

/** G13 — sichtbare Verschiebung, gedeckelt. */
export const SWIPE_MAX_TRAVEL_PX = 160

/** G13 — Hoehstdauer; ein langsamer Zug ist kein Wisch. */
export const SWIPE_MAX_DURATION_MS = 800

/** G13 — senkrechter Weg, der die Geste abbricht (unveraendert aus dem Bestand). */
export const SWIPE_VERTICAL_CANCEL_PX = 20

/**
 * G1 — liegt ein grober Zeiger an? Fail-closed: ohne `matchMedia` (jsdom ohne
 * Stub, sehr alte Browser) gilt die Geste als NICHT verfuegbar.
 *
 * 🔴 **Exportiert seit G0.9 (2026-10-12)** — der Auswahlmodus (§11.6) bindet
 * seinen Langdruck an **dieselbe** Pruefung. Eine zweite Abfrage daneben waere
 * die Stelle, an der die beiden Gesten auf einem Hybridgeraet auseinanderlaufen
 * (Trackpad + Touchscreen gilt als FEIN). Der Export ist rein additiv: die
 * Funktion behaelt Signatur und Verhalten.
 */
export function readCoarsePointer() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia(COARSE_POINTER_QUERY).matches === true
  } catch {
    return false
  }
}

export function useSwipeRow({
  onSwipeLeft,
  onSwipeRight,
  threshold = SWIPE_MIN_THRESHOLD_PX,
  thresholdFraction = SWIPE_THRESHOLD_FRACTION,
  maxDuration = SWIPE_MAX_DURATION_MS,
  verticalCancel = SWIPE_VERTICAL_CANCEL_PX,
  // G7 (G0.9, 2026-10-12) — `true` ist das BISHERIGE Verhalten; die Prop ist
  // rein additiv. Die drei Wachen unten stehen in Auf-, Bewegungs- und
  // Loslasspfad, damit auch ein Wechsel WAEHREND einer laufenden Geste greift —
  // ohne einen zusaetzlichen Effekt (der waere keine additive Aenderung).
  enabled = true,
}) {
  const startRef = useRef(null)
  const [dx, setDx] = useState(0)
  const [swiping, setSwiping] = useState(false)
  const [coarsePointer, setCoarsePointer] = useState(readCoarsePointer)

  // G1 — ein Tablet mit angeschlossener Maus wechselt zur LAUFZEIT die Zeigerart;
  // ein einmaliges Lesen beim Aufbau wuerde dort dauerhaft falsch liegen.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mql = window.matchMedia(COARSE_POINTER_QUERY)
    setCoarsePointer(mql.matches === true)
    const onChange = (event) => setCoarsePointer(event.matches === true)
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    if (typeof mql.addListener === 'function') { // Safari < 14
      mql.addListener(onChange)
      return () => mql.removeListener(onChange)
    }
    return undefined
  }, [])

  const reset = useCallback(() => {
    startRef.current = null
    setDx(0)
    setSwiping(false)
  }, [])

  const onPointerDown = useCallback((e) => {
    // G7 — im Auswahlmodus ist der Wisch pausiert: kein Start, also auch keine
    // Verschiebung und keine Ausfuehrung (G0.9).
    if (!enabled) return
    // G1 — ohne groben Zeiger gibt es keine Zeilengeste.
    if (!coarsePointer) return
    // Only primary button / first touch
    if (e.button != null && e.button !== 0) return

    // G3 — die Raender gehoeren dem System. Ein Zug, der dort beginnt, ist ein
    // normaler Tipp; die Zeile reagiert nicht.
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0
    if (viewportWidth > SWIPE_EDGE_GUARD_PX * 2) {
      if (e.clientX < SWIPE_EDGE_GUARD_PX) return
      if (e.clientX > viewportWidth - SWIPE_EDGE_GUARD_PX) return
    }

    // G5-B — die Schwelle haengt an der Zeilenbreite, gemessen am Element, auf
    // das die Handler gespreizt sind. In jsdom ist sie 0 → es bleibt der
    // px-Boden, und genau das ist auch auf einem schmalen Telefon richtig.
    const rowWidth = e.currentTarget?.getBoundingClientRect?.().width || 0

    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      t: Date.now(),
      axis: null,
      threshold: Math.max(threshold, thresholdFraction * rowWidth),
    }
  }, [coarsePointer, threshold, thresholdFraction, enabled])

  const onPointerMove = useCallback((e) => {
    // G7 — ein Wechsel WAEHREND der Geste laesst sie fallen (G0.9).
    if (!enabled) { reset(); return }
    const start = startRef.current
    if (!start) return
    const dxNow = e.clientX - start.x
    const dyNow = e.clientY - start.y

    // G4 — die Richtung faellt EINMAL, und sie faellt der DOMINANTEN Achse zu.
    // 🔴 Die 10 px allein genuegen NICHT: ein Daumen, der beim senkrechten
    // Rollen 10 px seitlich driftet, haette die Zeile sonst gekapert und eine
    // Server-Aktion ausgeloest. Waagerecht ist entschieden, wenn
    // `SWIPE_DIRECTION_LOCK_PX` erreicht ist UND die waagerechte Bewegung
    // mindestens so weit reicht wie die senkrechte (`|dx| >= |dy|`). Sonst
    // bricht `verticalCancel` ab (der Nutzer rollt), darunter bleibt sie offen.
    if (start.axis === null) {
      const waagerecht = Math.abs(dxNow)
      const senkrecht = Math.abs(dyNow)
      if (waagerecht >= SWIPE_DIRECTION_LOCK_PX && waagerecht >= senkrecht) start.axis = 'x'
      else if (senkrecht >= verticalCancel) { reset(); return }
      else return
    }
    if (start.axis !== 'x') return

    setSwiping(true)
    // G13 — Cap visual travel so the row can't fly off screen
    setDx(Math.max(-SWIPE_MAX_TRAVEL_PX, Math.min(SWIPE_MAX_TRAVEL_PX, dxNow)))
  }, [verticalCancel, reset, enabled])

  const onPointerUp = useCallback((e) => {
    // G7 — im Auswahlmodus fuehrt der Wisch nicht aus (G0.9).
    if (!enabled) { reset(); return }
    const start = startRef.current
    if (!start) { reset(); return }
    const dxFinal = e.clientX - start.x
    const dt = Date.now() - start.t
    if (start.axis === 'x' && dt < maxDuration && Math.abs(dxFinal) > start.threshold) {
      // G12 — Haptik nur, wenn es sie gibt, nur kurz, und NIE die einzige
      // Rueckmeldung: die Aktion selbst zeigt ihren Widerruf (G7/G9/G11).
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(SWIPE_HAPTIC_MS) } catch { /* Geraet lehnt ab — folgenlos */ }
      }
      if (dxFinal < 0) onSwipeLeft?.()
      else onSwipeRight?.()
    }
    reset()
  }, [maxDuration, onSwipeLeft, onSwipeRight, reset, enabled])

  const handlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: reset,
    onPointerLeave: reset,
    style: { touchAction: 'pan-y' }, // G2 — let vertical scroll work; we own horizontal
  }

  return { handlers, dx, swiping }
}
